<template>
  <div style="display:contents">
    <div class="agent-session-head" data-agent-session-head>
      <div class="agent-session-head-main">
        <div class="agent-session-copy">
          <div class="agent-session-title-row">
            <div class="agent-session-title" data-agent-thread-title>{{ currentSessionName }}</div>
            <WechatPill v-if="showWechatPill" :agent-id="currentAgentId" />
          </div>
          <div class="agent-session-sub" data-agent-thread-sub>Agent 对话 · 已进入会话</div>
        </div>
        <div class="agent-session-title-actions">
          <button class="agent-session-action-btn secondary" type="button" @click="openDetail">定时任务</button>
          <button class="agent-session-action-btn" type="button" @click="openTopicDialog">新建任务</button>
          <button class="chat-search-btn" type="button" aria-label="搜索聊天记录" @click.stop="toggleChatSearch">
            <svg viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="1.8"/><path d="M21 21l-4.35-4.35" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
          </button>
        </div>
      </div>
    </div>
    <div class="processing-canvas" id="processingCanvasResult">
      <div class="user-ask-row">
        <div class="user-ask-bubble" id="resultUserBubble">{{ content.userBubble }}</div>
      </div>
      <div class="agent-response">
        <div class="agent-brand">
          <div class="agent-brand-mark">{{ currentAgentProfile ? currentAgentProfile.mark : '' }}</div>
          <div class="agent-brand-name">{{ currentAgentProfile ? currentAgentProfile.name : 'Agent' }}</div>
        </div>
        <p class="agent-response-text" id="resultIntroReply">{{ content.agentReply }}</p>
        <div class="completed-steps">
          <div class="completed-step">{{ content.searchChip1 }}</div>
          <div class="completed-step">整理并呈现结果给用户</div>
        </div>
      </div>
      <div class="agent-response">
        <div class="agent-brand">
          <div class="agent-brand-mark">{{ currentAgentProfile ? currentAgentProfile.mark : '' }}</div>
          <div class="agent-brand-name">{{ currentAgentProfile ? currentAgentProfile.name : 'Agent' }}</div>
        </div>
        <p class="agent-response-text" id="resultAgentReply">这是为您整理的关于"{{ content.userBubble }}"的调研报告，请查阅附件。</p>
        <details class="result-report-card" id="resultReportCard" open>
          <summary class="result-report-head">
            <div class="result-report-icon"></div>
            <div class="result-report-title" id="resultReportTitle">{{ resultData.title }}</div>
            <div class="result-report-actions">
              <div class="result-report-more">···</div>
              <div class="result-report-chevron">⌃</div>
            </div>
          </summary>
          <div class="result-report-body">
            <h3 class="result-report-section-title">引言</h3>
            <p class="result-report-paragraph">随着人工智能技术的飞速发展，自主Agent（Autonomous Agents）作为能够独立感知环境、规划行动并执行复杂任务的智能系统，正逐步成为AI落地的重要方向。它们不仅能理解用户目标，还可以自主搜索信息、调用工具、协同任务与持续优化。</p>
            <p class="result-report-paragraph">在产品与研发领域，主流自主Agent框架已经开始覆盖从任务规划、记忆管理、工具调用到多Agent协作的完整链路。下面将从开发框架与代表性产品两个角度，对目前主流自主Agent生态做一个全面梳理。</p>
            <h3 class="result-report-subtitle">主流自主Agent开发框架</h3>
            <ol class="result-report-list">
              <li><strong>LangGraph</strong><br>LangGraph 是 LangChain 团队推出的开源框架，强调将复杂任务拆分为带状态的节点与边，适合构建多步骤、可回溯的 Agent 工作流。其优势在于状态管理清晰、流程编排能力强，并且方便和现有 LangChain 工具链整合。</li>
              <li><strong>AutoGen</strong><br>AutoGen 由微软研究团队推出，面向多智能体（Multi-Agent）协作场景。它强调多个角色化 Agent 之间的对话与任务协同，适合需要分工合作、连续推理和自动反馈的复杂任务。</li>
            </ol>
          </div>
          <div class="result-report-footer">
            <div class="result-thumb">
              <div class="result-thumb-line long"></div>
              <div class="result-thumb-line mid"></div>
              <div class="result-thumb-line short"></div>
              <div class="result-thumb-line long"></div>
            </div>
            <div class="completed-step">整理并呈现结果给用户</div>
          </div>
        </details>
      </div>
    </div>
    <div class="processing-footer result-mode">
      <div class="processing-composer" id="composerBoxResult">
        <div class="processing-editor">
          <textarea
            id="resultComposerInput"
            :placeholder="'发送消息给 ' + currentSessionName"
            :value="inputValue"
            @input="onInput"
            @keydown="onKeydown"
            ref="textarea"
          ></textarea>
        </div>
        <div class="composer-feedback-row" data-skill-feedback="result" :hidden="selectedSkills.length === 0">
          <span v-for="skill in selectedSkills" :key="skill.id" class="composer-skill-chip">
            <span class="composer-skill-chip-icon">
              <img v-if="isUrlIcon(skill.icon)" :src="skill.icon" alt="" @error="onIconError" />
              <span v-else>{{ skill.icon }}</span>
            </span>
            <span>{{ skill.name }}</span>
            <button class="composer-skill-chip-remove" type="button" @click="removeSkill(skill.id)">×</button>
          </span>
        </div>
        <div class="processing-toolbar">
          <div class="processing-left">
            <button class="tool-btn" type="button"><svg viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg><span class="tool-btn-label">添加文件</span></button>
            <button class="tool-btn" :class="{ 'skill-selected': selectedSkills.length > 0 }" type="button" data-skill-trigger="result" @click="openSkillPicker">
              <svg viewBox="0 0 24 24" fill="none"><path d="M12 3l2.4 4.86 5.36.78-3.88 3.78.92 5.34L12 15.77 7.2 17.76l.92-5.34L4.24 8.64l5.36-.78L12 3z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path></svg>
              <span class="tool-btn-label">使用技能</span>
              <span v-if="selectedSkills.length > 0" class="tool-btn-clear" @click.stop="clearAllSkills"><svg viewBox="0 0 24 24" fill="none"><path d="M8 8l8 8M16 8l-8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path></svg></span>
            </button>
            <button class="tool-btn composer-model-btn" type="button" data-model-trigger="result" @click="openModelPicker">
              <span class="tool-btn-label tool-btn-model-label">{{ selectedModel }}</span>
              <span class="composer-model-btn-caret">▾</span>
            </button>
          </div>
          <div class="processing-right">
            <!-- voice btn hidden --><!-- <button class="processing-icon-btn" :class="{ 'voice-active': voiceActive }" title="语音输入" @click="toggleVoice"><svg viewBox="0 0 24 24" fill="none"><path d="M12 3a3 3 0 013 3v6a3 3 0 11-6 0V6a3 3 0 013-3zM19 11a7 7 0 01-14 0M12 18v3M8 21h8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg></button> -->
            <button class="processing-send-btn" id="resultComposerSend" title="发送" @click="send">
              <svg viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path><path d="M22 2L15 22l-4-9-9-4 18-7z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import { getSpeechUnsupportedReason, startSpeechRecognition, stopSpeechRecognition } from '../utils/speech'
import WechatPill from './WechatPill.vue'

export default {
  name: 'AgentStateResult',
  components: { WechatPill },
  data() { return { voiceActive: false, voiceText: '', voiceError: '', _voiceBaseText: '' } },
  computed: {
    ...mapState(['processingContent', 'workspaceResultData', 'composerInputValues', 'currentSessionKey', 'sessionItems', 'currentTopicKey', 'sessionTopicPages']),
    ...mapGetters(['selectedSkills', 'currentAgentProfile', 'currentConversationId']),
    content() { return this.processingContent },
    resultData() { return this.workspaceResultData },
    inputValue() { return this.composerInputValues['result'] || '' },
    currentSessionName() {
      const s = this.sessionItems.find(i => i.key === this.currentSessionKey)
      return s ? s.name : '吴彦祖-产品经理'
    },
    currentAgentId() {
      const convId = this.currentConversationId
      const agents = this.$store.state.conversationAgents[convId] || []
      return agents.length ? agents[0].id : null
    },
    showWechatPill() {
      if (!this.currentAgentId) return false
      const item = this.sessionItems.find(s => s.key === this.currentSessionKey)
      if (!item) return false
      const pageKey = item.topicKey
      const page = pageKey ? this.sessionTopicPages[pageKey] : null
      if (!page || !page.topics || page.topics.length <= 1) return true
      const currentTopic = page.topics.find(t => t.key === this.currentTopicKey)
      return !!(currentTopic && currentTopic.isDefault)
    }
  },
  methods: {
    ...mapMutations(['SET_COMPOSER_INPUT', 'CLEAR_SKILLS']),
    ...mapActions(['sendConversationMessage']),
    toggleVoice() {
      if (this.voiceActive) {
        stopSpeechRecognition(); this.voiceActive = false
        if (this.voiceText) this.SET_COMPOSER_INPUT({ context: 'result', value: this._voiceBaseText + this.voiceText })
        this.voiceText = ''; this.voiceError = ''; return
      }
      const reason = getSpeechUnsupportedReason()
      if (reason) { this.voiceActive = true; this.voiceError = reason; return }
      this.voiceActive = true; this.voiceText = ''; this.voiceError = ''; this._voiceBaseText = this.inputValue || ''
      startSpeechRecognition(
        (text) => { this.voiceText = text },
        () => { if (this.voiceText) this.SET_COMPOSER_INPUT({ context: 'result', value: this._voiceBaseText + this.voiceText }); this.voiceActive = false; this.voiceText = ''; this.voiceError = '' },
        (err) => { this.voiceError = err || '语音识别失败'; this.voiceText = '' }
      )
    },
    onInput(e) { this.SET_COMPOSER_INPUT({ context: 'result', value: e.target.value }); this.autoResize() },
    onKeydown(e) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.send() } },
    send() {
      const val = this.inputValue.trim()
      if (!val) return
      const convId = this.currentConversationId
      if (convId) {
        this.sendConversationMessage({ conversationId: convId, text: val })
      }
      this.SET_COMPOSER_INPUT({ context: 'result', value: '' })
    },
    autoResize() {
      const ta = this.$refs.textarea
      if (!ta) return
      const lh = parseFloat(window.getComputedStyle(ta).lineHeight) || 24
      ta.style.height = 'auto'
      ta.style.height = Math.min(Math.max(ta.scrollHeight, lh * 2), lh * 10) + 'px'
    },
    openSkillPicker(e) {
      this.$store.commit('SET_MODEL_PICKER_OPEN', false)
      this.$store.commit('SET_SKILL_PICKER_OPEN', { open: true, rect: e.currentTarget.getBoundingClientRect() })
    },
    openModelPicker(e) {
      this.$store.commit('SET_SKILL_PICKER_OPEN', false)
      this.$store.commit('SET_MODEL_PICKER_OPEN', { open: true, rect: e.currentTarget.getBoundingClientRect() })
    },
    removeSkill(id) { this.CLEAR_SKILLS(id) },
    // skill.icon 后端要么 URL（http/https/data/blob/相对路径 /），要么 emoji/单字
    isUrlIcon(s) { return typeof s === 'string' && /^(https?:|data:|blob:|\/)/.test(s) },
    onIconError(e) { try { e.target.style.display = 'none' } catch (_) {} },
    clearAllSkills() { this.CLEAR_SKILLS() },
    openTopicDialog() { this.$store.commit('SET_GROUP_DIALOG', { open: true, mode: 'agent-topic', sessionKey: this.currentSessionKey }) },
    openDetail(e) {
      const rect = e.currentTarget.getBoundingClientRect()
      this.$store.dispatch('openAgentDetailPopover', { x: Math.max(0, rect.right - 194), y: rect.bottom + 8 })
    },
    toggleChatSearch() {
      const next = !this.$store.state.chatSearchOpen
      this.$store.commit('SET_CHAT_SEARCH_OPEN', next)
      if (next && !this.$store.state.workspaceOpen) {
        this.$store.commit('SET_WORKSPACE_OPEN', true)
      }
    }
  },
  mounted() { this.autoResize() }
}
</script>
